/**
 * What the user waited for, measured on one clock.
 *
 * A turn is not a request. One message is a `/chat` call, then one `/events`
 * call for every command the reply asks the page to run, each of which can
 * ask for another. The backend times its own legs; only the browser can see
 * the whole thing, and only the browser knows when the user pressed send.
 *
 * Every duration here is taken from `performance.now()` - a monotonic clock
 * that does not jump when the machine's wall clock is corrected. Wall time is
 * recorded once, as the turn's start, and never subtracted from a server
 * timestamp: the skew between a laptop and the backend is routinely seconds
 * and is not latency.
 */

const KEPT_TURNS = 20;

/** @type {Map<string, object>} recent turns, newest last */
const turns = new Map();

/**
 * Identifier for one turn. Not security-bearing - it only has to be unique
 * among a few dozen turns in one tab.
 * @return {string} turn id
 */
function mintTurnId() {
	const random = Math.random().toString(36).slice(2, 10);
	return `t_${Date.now().toString(36)}${random}`;
}

/**
 * Note that the user has sent a message, and start its clock.
 * @return {string} the new turn's id
 */
export function beginTurn() {
	const turnId = mintTurnId();
	turns.set(turnId, {
		turn_id: turnId,
		started_at: new Date().toISOString(),
		startedMark: performance.now(),
		marks: {},
		http_calls: 0,
		commands: [],
	});
	// Bounded so a long session cannot grow this without limit. The oldest
	// key is the first one insertion order returns.
	while (turns.size > KEPT_TURNS) turns.delete(turns.keys().next().value);
	return turnId;
}

/**
 * Record how long after send something happened.
 * @param {string} turnId turn identifier
 * @param {string} name what happened
 */
export function markTurn(turnId, name) {
	const turn = turns.get(turnId);
	if (!turn) return;
	// First writer wins: "first paint" is the first one, and a later bubble
	// in the same turn must not overwrite it.
	if (turn.marks[name] === undefined) {
		turn.marks[name] = Math.round(performance.now() - turn.startedMark);
	}
}

/**
 * Count one HTTP call made on this turn's behalf.
 * @param {string} turnId turn identifier
 */
export function countTurnCall(turnId) {
	const turn = turns.get(turnId);
	if (turn) turn.http_calls += 1;
}

/**
 * Record one browser command and what it cost.
 * @param {string} turnId turn identifier
 * @param {object} entry command outcome
 */
export function recordTurnCommand(turnId, entry) {
	const turn = turns.get(turnId);
	if (turn) turn.commands.push(entry);
}

/**
 * Close the turn and publish what it cost.
 *
 * Announced as an event rather than posted anywhere: there is no telemetry
 * endpoint, and inventing one to carry a number the page already has would
 * add a request to every turn we are trying to measure.
 * @param {string} turnId turn identifier
 * @return {object|null} the completed turn
 */
export function endTurn(turnId) {
	const turn = turns.get(turnId);
	if (!turn || turn.total_ms !== undefined) return turn || null;
	turn.total_ms = Math.round(performance.now() - turn.startedMark);
	delete turn.startedMark;
	window.dispatchEvent(
		new CustomEvent('canvas-agent:turn-completed', {detail: turn}),
	);
	return turn;
}

/**
 * The turns this tab has seen, newest last. For the debug view.
 * @return {object[]} recent turns
 */
export function recentTurns() {
	return [...turns.values()];
}


/**
 * What this turn cost the user, as numbers fit to send.
 *
 * L05. The browser has always measured this and always kept it to itself,
 * so a backend trace could account for its own seconds and not for the ones
 * the user actually waited - the gap between "the model took 12s" and "the
 * turn took 30s".
 *
 * Numbers only. Nothing here names a capability, a record or anything the
 * user typed, which is what makes it safe to put on an ordinary event.
 *
 * @param {string} turnId browser turn id
 * @return {object|null} timing summary, or null when there is nothing to say
 */
export function turnTimingSummary(turnId) {
	const turn = turns.get(turnId);
	if (!turn) return null;
	const summary = {};
	// total_ms and http_calls sit on the turn; everything else is a mark,
	// which is where markTurn puts them. Reading both off the turn found
	// only the first two and silently dropped the rest.
	['total_ms', 'http_calls'].forEach((key) => {
		if (Number.isFinite(turn[key])) summary[key] = Math.round(turn[key]);
	});
	// How much of the user's wait had passed when this was sent.
	//
	// total_ms is absent on every event that matters: endTurn runs after the
	// command loop, and the events are sent inside it - so a summary keyed
	// on total_ms carried nothing for exactly the turns worth measuring.
	// This is available at any moment and is not the same claim: it is time
	// elapsed so far, not the total, and the backend keeps it under its own
	// name so neither is mistaken for the other.
	if (Number.isFinite(turn.startedMark)) {
		summary.elapsed_ms = Math.round(performance.now() - turn.startedMark);
	} else if (Number.isFinite(turn.total_ms)) {
		// endTurn removes the start mark, so a turn that has already closed
		// has no "so far" left to measure - its elapsed time is its total.
		// Reported anyway so the field is on every event and a timeline
		// never has to guess which shape it received.
		summary.elapsed_ms = Math.round(turn.total_ms);
	}
	Object.entries(turn.marks || {}).forEach(([name, value]) => {
		if (Number.isFinite(value)) summary[name] = Math.round(value);
	});
	return Object.keys(summary).length ? summary : null;
}
